import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE = 300;

const VIEWS = ['history', 'favorites', 'all'];

const DEFAULT_STATE = {
    view: 'history',      // 'history' | 'favorites' | 'all'
    search: '',
    categoryIds: [],      // array of numeric ids
    inStockOnly: false,
    sortBy: null,         // 'sku' | 'name' | 'price' | 'stock' | 'category' | null
    sortOrder: null,      // 'asc' | 'desc' | null
    page: 1,
};

function storageKey(customerId) {
    return customerId ? `b2b.catalogue.${customerId}` : null;
}

function readStored(customerId) {
    const key = storageKey(customerId);
    if (!key) return null;
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        return {
            view: VIEWS.includes(parsed.view) ? parsed.view : 'history',
            categoryIds: Array.isArray(parsed.categoryIds)
                ? parsed.categoryIds.filter(n => Number.isInteger(n) && n > 0)
                : [],
            inStockOnly: Boolean(parsed.inStockOnly),
            sortBy: ['sku', 'name', 'price', 'stock', 'category'].includes(parsed.sortBy)
                ? parsed.sortBy : null,
            sortOrder: parsed.sortOrder === 'asc' || parsed.sortOrder === 'desc'
                ? parsed.sortOrder : null,
        };
    } catch {
        return null;
    }
}

function writeStored(customerId, state) {
    const key = storageKey(customerId);
    if (!key) return;
    try {
        localStorage.setItem(key, JSON.stringify({
            view: state.view,
            categoryIds: state.categoryIds,
            inStockOnly: state.inStockOnly,
            sortBy: state.sortBy,
            sortOrder: state.sortOrder,
        }));
    } catch {
        // QuotaExceeded or blocked storage: preferences simply won't persist.
    }
}

/**
 * Catalogue filter state with per-customer localStorage persistence.
 * Handles debounced search separately so typing doesn't thrash the server.
 * Page resets to 1 whenever a filter/sort/search/view changes.
 */
export default function useCatalogueFilters(customerId) {
    const [state, setState] = useState(() => {
        const stored = readStored(customerId);
        return stored ? { ...DEFAULT_STATE, ...stored } : DEFAULT_STATE;
    });
    const [searchRaw, setSearchRaw] = useState('');

    // Hydrate once when customerId arrives (AuthContext loads after mount).
    const hydratedFor = useRef(null);
    useEffect(() => {
        if (!customerId || hydratedFor.current === customerId) return;
        hydratedFor.current = customerId;
        const stored = readStored(customerId);
        if (stored) setState(prev => ({ ...prev, ...stored, page: 1 }));
    }, [customerId]);

    // Persist on every material change.
    useEffect(() => {
        writeStored(customerId, state);
    }, [customerId, state.view, state.categoryIds, state.inStockOnly, state.sortBy, state.sortOrder]);

    // Debounce the search input into state.search.
    useEffect(() => {
        const id = setTimeout(() => {
            setState(prev => prev.search === searchRaw.trim()
                ? prev
                : { ...prev, search: searchRaw.trim(), page: 1 });
        }, SEARCH_DEBOUNCE);
        return () => clearTimeout(id);
    }, [searchRaw]);

    const setView = useCallback((view) => {
        if (!VIEWS.includes(view)) return;
        setState(prev => (prev.view === view ? prev : { ...prev, view, page: 1 }));
    }, []);

    const setCategoryIds = useCallback((ids) => {
        const clean = (ids || []).filter(n => Number.isInteger(n) && n > 0);
        setState(prev => ({ ...prev, categoryIds: clean, page: 1 }));
    }, []);

    const setInStockOnly = useCallback((value) => {
        setState(prev => ({ ...prev, inStockOnly: Boolean(value), page: 1 }));
    }, []);

    // Cycle: null → asc → desc → null on repeated clicks on the same column.
    // Clicking a new column starts at asc.
    const cycleSort = useCallback((column) => {
        setState(prev => {
            if (prev.sortBy !== column) return { ...prev, sortBy: column, sortOrder: 'asc', page: 1 };
            if (prev.sortOrder === 'asc') return { ...prev, sortOrder: 'desc', page: 1 };
            return { ...prev, sortBy: null, sortOrder: null, page: 1 };
        });
    }, []);

    const setPage = useCallback((page) => {
        setState(prev => (prev.page === page ? prev : { ...prev, page }));
    }, []);

    const clearAll = useCallback(() => {
        setSearchRaw('');
        setState({ ...DEFAULT_STATE, view: 'all' });
    }, []);

    // Params shape sent to GET /b2b/products. Memoized so the fetch effect key is stable.
    const queryParams = useMemo(() => {
        const params = {
            limit: PAGE_SIZE,
            offset: (state.page - 1) * PAGE_SIZE,
        };
        if (state.search) params.search = state.search;
        if (state.view === 'history') params.my_history = '1';
        if (state.view === 'favorites') params.favorites = '1';
        if (state.categoryIds.length > 0) params.category_ids = state.categoryIds.join(',');
        if (state.inStockOnly) params.in_stock = '1';
        if (state.sortBy && state.sortOrder) {
            params.sort_by = state.sortBy;
            params.sort_order = state.sortOrder;
        }
        return params;
    }, [state]);

    return {
        state,
        searchRaw,
        setSearchRaw,
        setView,
        setCategoryIds,
        setInStockOnly,
        cycleSort,
        setPage,
        clearAll,
        queryParams,
        pageSize: PAGE_SIZE,
    };
}
